<script setup lang='ts'>
import { APP_ROUTER } from '@qctsw/common'
import { useComplaintFilter, useSearchCollapseControl } from './hooks/filter'
import SearchList from './components/SearchList.vue'
import { carLevel, seriesEnergy } from '@/pages/reputation/hooks/filter'
import { CAR_INSURANCE_CODE, ISSUE_TYPE_CODE_ENUM } from '@/shared/constants'

const excludeKeys = ['carBrandInfo', 'carSeriesInfo', 'quality', 'sale', 'afterSale', 'appeal', 'part_1', 'part_2', 'part_3', 'part_4']

const router = useRouter()
const {
  formRef,
  formData,
  carSelected,
  pickerChangeShow,
  filterRouteQuery,
  filterParams,
  getShowValue,
  clearFilter,
  initRouteQuery,
  // 问题选项
  issuesState,
  issues,
  getIssuesHandle,
  // 零件选项
  partsState,
  parts,
  getPartsHandle,
} = useComplaintFilter()

const { show, autoShow, filterFormRef } = useSearchCollapseControl()
function submit() {
  show.value = false
  router.push(APP_ROUTER.complaintSearch({ ...filterRouteQuery.value }))
}

async function initHandler() {
  initRouteQuery()
  await Promise.all([
    getIssuesHandle(),
    getPartsHandle(),
  ])
}

provide('complaintSearchFilterParams', filterParams)
provide('complaintSearchFilterRouteQuery', filterRouteQuery)

useActivatedWatch(() => [
  watch(autoShow, () => {
    pickerChangeShow.value = false
    requestAnimationFrame(() => {
      pickerChangeShow.value = true
    })
  }),
])

useActivated({
  activatedFn: initRouteQuery,
})

await initHandler()
</script>

<template>
  <div class="bg-slate-1">
    <div class="public-container public-page">
      <NAlert type="info" closable>
        只显示最近三年内的投诉（编号搜索除外）
      </NAlert>
      <div class="bg-white p-sm rounded-md relative mt-sm">
        <div ref="filterFormRef" class="flex items-center justify-between cursor-pointer" @click="show = !show">
          <h2 class="flex items-end">
            <span>查询条件</span>
            <span class="text-3 text-slate-4 ml-1">点击{{ show ? '收起' : '展开' }}</span>
          </h2>
          <ClientOnly>
            <div class="text-3.5 flex items-center text-slate-4" @click.stop>
              <NSwitch v-if="pickerChangeShow" v-model:value="autoShow" />
              <span class="ml-2">向上滚动时自动展开查询条件</span>
            </div>
          </ClientOnly>
        </div>
        <NCollapseTransition :show="show">
          <NForm
            ref="formRef"
            :model="formData"
            :label-width="120"
            label-placement="left"
            size="medium"
            class="mt-sm"
          >
            <div class="flex items-center mb-sm ">
              <NFormItem label="投诉标题" :show-feedback="false" class="w-100">
                <NInput v-model:value="formData.title" placeholder="投诉标题" />
              </NFormItem>
              <NFormItem label="投诉id" :show-feedback="false">
                <NInput v-model:value="formData.id" placeholder="投诉id" />
              </NFormItem>
            </div>
            <NFormItem label="品牌车系" :show-feedback="false" class="mb-sm">
              <BasicCarSelectFilter
                v-model:brandId="formData.carBrandId"
                v-model:seriesId="formData.carSeriesId"
                :show-footer="true"
                :select-level="2"
                @change="carSelected"
              />
            </NFormItem>
            <NFormItem label="车型属性" :show-feedback="false" class="mb-sm">
              <BasicRadioGroup v-model="formData.seriesGrade" :options="carLevel" />
            </NFormItem>

            <NFormItem label="动力类型" :show-feedback="false" class="mb-sm">
              <BasicRadioGroup v-model="formData.seriesEnergyTypeEnum" :options="seriesEnergy" />
            </NFormItem>

            <NFormItem label="出故障时间" :show-feedback="false" class="mb-sm">
              <NDatePicker v-if="pickerChangeShow" v-model:value="formData.timePicker" value-format="yyyy-MM-dd" type="daterange" clearable />
            </NFormItem>
            <UtilLoadState :state="issuesState" :reload-handler="getIssuesHandle">
              <NFormItem label="质量问题" :show-feedback="false" class="mb-sm">
                <BasicCheckBoxGroup v-model="formData.quality" :options="issues[ISSUE_TYPE_CODE_ENUM.quality]" />
              </NFormItem>
              <NFormItem label="销售问题" :show-feedback="false" class="mb-sm">
                <BasicCheckBoxGroup v-model="formData.sale" :options="issues[ISSUE_TYPE_CODE_ENUM.sale]" />
              </NFormItem>
              <NFormItem label="售后问题" :show-feedback="false" class="mb-sm">
                <BasicCheckBoxGroup v-model="formData.afterSale" :options="issues[ISSUE_TYPE_CODE_ENUM.afterSale]" />
              </NFormItem>
              <NFormItem label="诉求" :show-feedback="false" class="mb-sm">
                <BasicCheckBoxGroup v-model="formData.appeal" :options="issues[ISSUE_TYPE_CODE_ENUM.appeal]" />
              </NFormItem>
            </UtilLoadState>
            <NFormItem label="问题地址" :show-feedback="false" class="mb-sm">
              <BasicAreaSelect
                v-if="pickerChangeShow"
                v-model:province-id="formData.areaIds[0]"
                v-model:city-id="formData.areaIds[1]"
                class="max-w-100"
                :is-area="false"
              />
            </NFormItem>
            <NFormItem label="车辆状态" :show-feedback="false" class="mb-sm">
              <BasicRadioGroup v-model="formData.insuranceCode" :options="CAR_INSURANCE_CODE" />
            </NFormItem>
            <NFormItem label="配件品牌" :show-feedback="false" class="mb-sm">
              <UtilLoadState :state="partsState" :reload-handler="getPartsHandle">
                <div class="grid grid-cols-4 gap-xl w-full pr-xl">
                  <NSelect
                    v-if="pickerChangeShow" v-model:value="formData.part_1" multiple max-tag-count="responsive"
                    :options="parts[1]" placeholder="请选择轮胎品牌"
                  />
                  <NSelect
                    v-if="pickerChangeShow" v-model:value="formData.part_2" multiple max-tag-count="responsive"
                    :options="parts[2]" placeholder="请选择机油品牌"
                  />
                  <NSelect
                    v-if="pickerChangeShow" v-model:value="formData.part_3" multiple max-tag-count="responsive"
                    :options="parts[3]" placeholder="请选择变速箱油品牌"
                  />
                  <NSelect
                    v-if="pickerChangeShow" v-model:value="formData.part_4" multiple max-tag-count="responsive"
                    :options="parts[4]" placeholder="请选择传动油品牌"
                  />
                </div>
              </UtilLoadState>
            </NFormItem>
          </NForm>
        </NCollapseTransition>
        <div class="border-t-1 border-slate-2 mt-sm pt-sm">
          <NFormItem :show-feedback="false" label-placement="left" label="已选条件">
            <div>
              <template v-for="value, key in formData" :key="key">
                <button
                  v-if="value !== undefined && !excludeKeys.includes(key) && getShowValue(key)"
                  class="mr-sm btn-primary"
                  @click="clearFilter(key)"
                >
                  {{ getShowValue(key) }}
                </button>
                <template v-else-if="['quality', 'sale', 'afterSale', 'appeal', 'part_1', 'part_2', 'part_3', 'part_4'].includes(key) && getShowValue(key)?.length">
                  <button
                    v-for="item, index in (getShowValue(key) as any)"
                    :key="index" class="mr-sm btn-primary"
                    @click="clearFilter(key, item.value)"
                  >
                    {{ item.label }}
                  </button>
                </template>
              </template>
              <button class="btn-default" @click="clearFilter()">
                全部清除
              </button>
            </div>
          </NFormItem>
          <div class="flex items-center justify-center mt-3xl relative">
            <NButton type="primary" class="mr-sm px-20" @click="submit">
              搜索
            </NButton>
          </div>
        </div>
      </div>
      <div class="border-1 border-gray-1 flex items-center mt-sm py-4 rounded-md bg-white">
        <BasicStepComplaint class="pl-5 pr-5 flex-grow min-w-0" />
        <NuxtLink
          class="mx-5 bg-orange-6 text-white px-4xl py-2.5 rounded-full text-5.5 hover:bg-orange-5"
          :to="APP_ROUTER.publish()"
        >
          我要投诉
        </NuxtLink>
      </div>
      <SearchList
        :filter-params="filterParams"
        :filter-route-query="filterRouteQuery"
      />
    </div>
  </div>
</template>

<style lang='scss' scoped>
:deep(*){
  .n-form-item-label--right-mark{
    --uno: text-4 font-bold;
  }
}
</style>
